import React, { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import { toast } from '@src/components/core/toaster';
import { useAuth } from '@src/context';
import { listApiKeys, createApiKey, revokeApiKey } from '@src/api/clientApiKeys';

const API_BASE = window.location.origin;

const ENDPOINTS = [
  {
    method: 'GET',
    path: '/api/v1/clients/',
    description: 'List all accessible clients (useful for parent-company keys)',
    params: [],
  },
  {
    method: 'GET',
    path: '/api/v1/client/',
    description: 'Single client profile',
    params: ['client (slug)'],
  },
  {
    method: 'GET',
    path: '/api/v1/leads/',
    description: 'Paginated list of leads',
    params: ['client (slug)', 'date_after', 'date_before', 'success_post', 'vertical', 'territory', 'search', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/leads/{id}/',
    description: 'Single lead detail',
    params: [],
  },
  {
    method: 'GET',
    path: '/api/v1/backend-data/',
    description: 'Paginated list of backend data records',
    params: ['client (slug)', 'date_after', 'date_before', 'vertical', 'territory', 'status', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/backend-data/{uuid}/',
    description: 'Single backend data record',
    params: [],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/client/',
    description: 'Overall client-level daily totals',
    params: ['client (slug)', 'date_after', 'date_before', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/vertical/',
    description: 'Daily metrics broken down by vertical',
    params: ['client (slug)', 'date_after', 'date_before', 'vertical', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/channel/',
    description: 'Daily metrics broken down by channel',
    params: ['client (slug)', 'date_after', 'date_before', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/territory/',
    description: 'Daily metrics broken down by territory',
    params: ['client (slug)', 'date_after', 'date_before', 'territory', 'vertical', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/dma/',
    description: 'Daily metrics broken down by DMA',
    params: ['client (slug)', 'date_after', 'date_before', 'dma_id', 'vertical', 'ordering', 'page', 'page_size'],
  },
  {
    method: 'GET',
    path: '/api/v1/summary/state/',
    description: 'Daily metrics broken down by state',
    params: ['client (slug)', 'date_after', 'date_before', 'state', 'vertical', 'ordering', 'page', 'page_size'],
  },
];

function MethodChip({ method }) {
  const colorMap = { GET: 'success', POST: 'primary', DELETE: 'error', PATCH: 'warning' };
  return (
    <Chip
      label={method}
      size="small"
      color={colorMap[method] || 'default'}
      sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}
    />
  );
}

function CopyButton({ value, sx }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={sx}>
        {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function CodeBlock({ children }) {
  return (
    <Box
      component="pre"
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.100',
        p: 2,
        borderRadius: 1,
        overflowX: 'auto',
        fontSize: 12,
        fontFamily: 'monospace',
        lineHeight: 1.6,
        m: 0,
      }}
    >
      {children}
    </Box>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function APIAccessPage() {
  const { state } = useAuth();
  const user = state?.user || {};
  const memberships = user.client_memberships || [];
  const isStaff = user.is_staff || user.is_superuser;
  const primaryMembership = memberships[0] || {};
  const clientId = primaryMembership.client_id || null;
  const parentCompanyId = primaryMembership.parent_company_id || null;
  const memberRole = primaryMembership.role || 'viewer';
  const canManageKeys = isStaff || memberRole === 'admin' || memberRole === 'editor';
  const isParentScope = !clientId && !!parentCompanyId;
  const [keys, setKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Generate key dialog
  const [generateOpen, setGenerateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [generating, setGenerating] = useState(false);

  // Newly-created key reveal dialog
  const [createdKey, setCreatedKey] = useState(null);
  const [keyVisible, setKeyVisible] = useState(false);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // API reference accordion
  const [refExpanded, setRefExpanded] = useState(false);

  const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || 'An unexpected error occurred.';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
      const val = data[firstKey];
      return `${firstKey}: ${Array.isArray(val) ? val[0] : val}`;
    }
    return 'An unexpected error occurred.';
  };

  const fetchKeys = useCallback(async () => {
    if (!clientId && !parentCompanyId) return;
    setLoadingKeys(true);
    setLoadError(null);
    try {
      const res = await listApiKeys({ clientId, parentCompanyId });
      setKeys(res.data?.results ?? res.data ?? []);
    } catch (err) {
      setLoadError(extractErrorMessage(err));
    } finally {
      setLoadingKeys(false);
    }
  }, [clientId, parentCompanyId]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleGenerate = useCallback(async () => {
    if (!clientId && !parentCompanyId) return;
    setGenerating(true);
    try {
      const payload = { label: newLabel.trim() };
      if (isParentScope) payload.parent_company_id = parentCompanyId;
      else payload.client_id = clientId;
      const res = await createApiKey(payload);
      const created = res.data;
      setKeys((prev) => [created, ...prev]);
      setGenerateOpen(false);
      setNewLabel('');
      setCreatedKey(created);
      setKeyVisible(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  }, [clientId, parentCompanyId, isParentScope, newLabel]);

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeApiKey(revokeTarget.id);
      setKeys((prev) => prev.filter((k) => k.id !== revokeTarget.id));
      setRevokeTarget(null);
      toast.success('API key revoked.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRevoking(false);
    }
  }, [revokeTarget]);

  const exampleKey = createdKey?.key || 'YOUR_API_KEY';
  const exampleCurl = `curl -H "X-API-Key: ${exampleKey}" \\
  "${API_BASE}/api/v1/leads/?date_after=2025-01-01&date_before=2025-01-31"`;

  return (
    <>
      <Helmet>
        <title>API Access | {config.SITE.title}</title>
      </Helmet>

      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>

            {/* Page header */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <KeyIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h4" fontWeight={700}>API Access</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Use API keys to pull your dashboard metrics and lead data into your own tools or CRM — no login required.
              </Typography>
            </Stack>

            {/* Keys card */}
            <Card>
              <CardHeader
                title="API Keys"
                subheader={isParentScope
                  ? 'Keys are scoped to your parent company and grant access to all child clients. Store securely — shown only once on creation.'
                  : 'Each key is scoped to this client only. Store it securely — it is shown only once on creation.'
                }
                action={
                  canManageKeys && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setGenerateOpen(true)}
                      disabled={!clientId && !parentCompanyId}
                    >
                      Generate Key
                    </Button>
                  )
                }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {loadingKeys && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {loadError && (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="error">{loadError}</Alert>
                  </Box>
                )}
                {!loadingKeys && !loadError && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Last Used</TableCell>
                        {canManageKeys && <TableCell align="right">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {keys.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canManageKeys ? 6 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No API keys yet.
                            {canManageKeys && (
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setGenerateOpen(true)}
                                sx={{ ml: 1 }}
                              >
                                Generate your first key
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        keys.map((key) => (
                          <TableRow key={key.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {key.label || <Typography component="span" variant="body2" color="text.secondary">Unlabeled</Typography>}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {key.scope === 'parent_company'
                                  ? key.parent_company_name || 'Parent Company'
                                  : key.client_name || 'Client'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={key.is_active ? 'Active' : 'Revoked'}
                                size="small"
                                color={key.is_active ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(key.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(key.last_used_at)}
                              </Typography>
                            </TableCell>
                            {canManageKeys && (
                              <TableCell align="right">
                                {key.is_active && (
                                  <Tooltip title="Revoke key">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setRevokeTarget(key)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* API Reference — collapsible */}
            <Card>
              <CardHeader
                title="API Reference"
                subheader="Authentication and available endpoints"
                action={
                  <IconButton
                    onClick={() => setRefExpanded((v) => !v)}
                    sx={{ transform: refExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                }
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setRefExpanded((v) => !v)}
              />
              <Collapse in={refExpanded}>
                <Divider />
                <CardContent>
                  <Stack spacing={4}>

                    {/* Authentication */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Authentication
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Include your API key in the <code>X-API-Key</code> request header. All endpoints are read-only.
                        Parent-company keys return data across all child clients by default — use <code>?client=slug</code> to narrow to one client.
                      </Typography>
                      <CodeBlock>{`curl -H "X-API-Key: YOUR_API_KEY" \\
  "${API_BASE}/api/v1/leads/"

# Parent-company key — filter to a specific client by slug
curl -H "X-API-Key: YOUR_API_KEY" \\
  "${API_BASE}/api/v1/leads/?client=my-client-slug"`}
                      </CodeBlock>
                    </Box>

                    {/* Endpoints table */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Endpoints
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 60 }}>Method</TableCell>
                            <TableCell>Path</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Query params</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ENDPOINTS.map((ep) => (
                            <TableRow key={ep.path} hover>
                              <TableCell><MethodChip method={ep.method} /></TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                                  {ep.path}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{ep.description}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace" fontSize={11} color="text.secondary">
                                  {ep.params.length ? ep.params.join(', ') : '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>

                    {/* Example */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Example — fetch leads for a date range
                      </Typography>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <Box sx={{ flex: 1 }}>
                          <CodeBlock>{exampleCurl}</CodeBlock>
                        </Box>
                        <CopyButton value={exampleCurl} sx={{ mt: 0.5 }} />
                      </Stack>
                    </Box>

                    {/* Pagination note */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Pagination
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        List endpoints return paginated results. Use <code>page</code> and <code>page_size</code> (max 500 for leads, 1000 for summary).
                        The response includes <code>count</code>, <code>next</code>, and <code>previous</code> fields.
                      </Typography>
                    </Box>

                  </Stack>
                </CardContent>
              </Collapse>
            </Card>

          </Stack>
        </Container>
      </Box>

      {/* Generate key dialog */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Label (optional)"
              placeholder="e.g. CRM integration"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              inputProps={{ maxLength: 100 }}
            />
            <Alert severity="info" icon={false}>
              The key will be shown <strong>once</strong> immediately after creation. Copy it before closing.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGenerateOpen(false); setNewLabel(''); }} disabled={generating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating}>
            {generating ? <CircularProgress size={16} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New key reveal dialog */}
      <Dialog open={!!createdKey} onClose={() => setCreatedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon color="warning" />
            <span>Save your API key</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              This key will <strong>not</strong> be shown again. Copy it now and store it securely.
            </Alert>
            <TextField
              value={createdKey?.key || ''}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: 13 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={keyVisible ? 'Hide' : 'Show'}>
                        <IconButton size="small" onClick={() => setKeyVisible((v) => !v)}>
                          {keyVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <CopyButton value={createdKey?.key || ''} />
                    </Stack>
                  </InputAdornment>
                ),
                type: keyVisible ? 'text' : 'password',
              }}
            />
            {createdKey?.label && (
              <Typography variant="body2" color="text.secondary">
                Label: <strong>{createdKey.label}</strong>
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>
            Done — I've saved my key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to revoke{' '}
            <strong>{revokeTarget?.label || 'this key'}</strong>?
            Any integrations using it will immediately stop working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? <CircularProgress size={16} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
